import React, { useState, useEffect, useMemo, useRef, useContext, FC } from "react"
import Styles from "../styles/TrimVideo.module.scss"
import clsx from "clsx";
import { useMoveObjectVector } from "../hooks/useMoveObjectVector"
import { ToastContext } from "../context/ToastContext"
import { changeMillisec2Minute } from "../utils/utilFunctions"
import GeneralButton from "./atoms/GeneralButton"
import { useCaptureList } from "../hooks/useCaptureList"
import ActivityIndicator from "./atoms/ActivityIndicator"
import ffmpeg from "../utils/FFmpeg"

const CaptureImageNumber = 5
const RangeMax = 10000

type Props = {
    file: File,
    setTrimmedVideo: ( video: Blob ) => void,
    visible: boolean,
    closeModal: () => void
}

export const VideoTrimModal: FC<Props>
    = ( {
        file,
        setTrimmedVideo,
        visible,
        closeModal
    } ) => {

        const [ trimStartTime, setTrimStartTime ] = useState( 0 );
        const [ trimEndTime, setTrimEndTime ] = useState( 0 );
        const [ videoDuration, setVideoDuration ] = useState( 0 );
        const [ changeVideoDuration, setChangeVideoDuration ] = useState<Date | null>( null );
        const [ videoTrimming, setVideoTrimming ] = useState( false );
        const [ durationBarWidth, setDurationBarWidth ] = useState( 500 );
        const [ videoCurrentTime, setVideoCurrentTime ] = useState( 0 );
        const [ trimIsDone, setTrimIsDone ] = useState( false );
        const videoRef = useRef<HTMLVideoElement>( null )
        const durationBarRef = useRef<HTMLDivElement>( null )
        const rightPinchRef = useRef<HTMLDivElement>( null )
        const leftPinchRef = useRef<HTMLDivElement>( null )
        const trimStartTimeRef = useRef( trimStartTime )
        const trimEndTimeRef = useRef( trimEndTime )
        const { addToast } = useContext( ToastContext )

        useEffect( () => {
            visible && ffmpeg.load()
        }, [ visible ] )

        useEffect( () => {
            !visible && videoRef.current?.pause()
        }, [ visible ] )

        useEffect( () => {
            trimStartTimeRef.current = trimStartTime
        }, [ trimStartTime ] )

        useEffect( () => {
            trimEndTimeRef.current = trimEndTime
        }, [ trimEndTime ] )

        const { objectMoveVector: rightPinchMoveVector, ...rightPinchTouchEvent } = useMoveObjectVector( { object: rightPinchRef.current, onStart: () => pauseVideo(), onDragEnd: () => playVideo(), initializeKey: file.name } )
        const { objectMoveVector: leftPinchMoveVector, ...leftPinchTouchEvent } = useMoveObjectVector( { object: rightPinchRef.current, onStart: () => pauseVideo(), onDragEnd: () => playVideo(), initializeKey: file.name } )

        const Capture = useCaptureList( {
            videoFile: file,
            // canStart: visible,
            captureWidth: durationBarWidth / CaptureImageNumber,
            CaptureImageNumber
        } )

        useEffect( () => {
            setVideoDuration( 0 )
            setTrimStartTime( 0 )
            setTrimEndTime( 0 )
        }, [ file ] )

        useEffect( () => {
            videoDuration && setTrimEndTime( videoDuration )
            setChangeVideoDuration( new Date() )
        }, [ videoDuration ] )

        useEffect( () => {
            if ( !durationBarRef.current ) return
            visible && setDurationBarWidth( durationBarRef.current.getBoundingClientRect().width )
        }, [ durationBarRef.current, visible ] )

        useEffect( () => {
            videoRef.current
                && trimEndTimeRef.current <= videoCurrentTime
                && restart()
        }, [ videoCurrentTime ] )

        useEffect( () => {
            if ( !durationBarWidth ) return
            if ( !leftPinchMoveVector ) return
            const startPosition = leftPinchMoveVector.X / durationBarWidth
            const startTime = videoDuration * startPosition
            setTrimStartTime( startTime )
            if ( videoRef.current ) videoRef.current.currentTime = startTime
        }, [ leftPinchMoveVector ] )

        useEffect( () => {
            setTrimIsDone( false )
        }, [ trimStartTime, trimEndTime ] )

        useEffect( () => {
            if ( !durationBarWidth ) return
            if ( !rightPinchMoveVector ) return
            const endPosition = 1 - ( -rightPinchMoveVector.X / durationBarWidth )
            const endTime = videoDuration * endPosition
            setTrimEndTime( endTime )
            if ( videoRef.current ) videoRef.current.currentTime = endTime
        }, [ rightPinchMoveVector ] )

        const pauseVideo = () => videoRef.current && videoRef.current.pause()
        const playVideo = () => videoRef.current && videoRef.current.play()

        const onChangeRangeValue = ( value: number ) => {
            if ( !videoRef.current ) return
            videoRef.current.currentTime = videoDuration * value / RangeMax
        }

        const trim = async () => {
            const { startTime, endTime } = getTrimTime()
            setVideoTrimming( true )
            const data = await ffmpeg.trim( {
                startTime,
                endTime,
                file
            } )
            if ( !data ) {
                addToast( { text: "トリミングに失敗しました", type: "error", position: 'center', } )
                setVideoTrimming( false )
                return
            }
            addToast( { text: "トリミングが完了しました", type: "success", position: 'center', } )
            setTrimmedVideo( new Blob( [ data.buffer ], { type: 'video/mp4' } ) )
            setVideoTrimming( false )
            setTrimIsDone( true )
            closeModal()
        }

        const getTrimTime = () => {
            if ( !leftPinchMoveVector || !rightPinchMoveVector ) return {
                startTime: 0,
                endTime: videoDuration
            }
            const startPosition = leftPinchMoveVector.X / durationBarWidth
            const endPosition = 1 - ( -rightPinchMoveVector.X / durationBarWidth )
            const startTime = videoDuration * startPosition
            const endTime = videoDuration * endPosition
            return {
                startTime,
                endTime
            }
        }

        const setTimeUpdate = () => videoRef.current && videoRef.current.addEventListener( "timeupdate",
            () => videoRef.current && setVideoCurrentTime( videoRef.current.currentTime )
        )

        const setOnReachEnd = () => videoRef.current && videoRef.current.addEventListener( "ended",
            () => restart()
        )

        const restart = () => {
            if ( !videoRef.current ) return
            videoRef.current.currentTime = trimStartTimeRef.current
            playVideo()
        }

        const VideoMemo = useMemo( () =>
            <div className={ Styles.video_container }>
                <video
                    ref={ videoRef }
                    className={ Styles.video }
                    src={ window.URL.createObjectURL( file ) }
                    muted={ true }
                    controls={ true }
                    loop={ false }
                    onLoadedData={ () => {
                        if ( !videoRef.current ) return
                        setVideoDuration( videoRef.current.duration )
                        videoRef.current.play()
                        setTimeUpdate()
                        setOnReachEnd()
                    } }
                />
            </div>
            , [ file ] )

        const LeftPinchMemo = useMemo( () =>
            <div
                className={ clsx( Styles.pinch, Styles.left_pinch ) }
                ref={ leftPinchRef }
                { ...leftPinchTouchEvent }
                style={ leftPinchMoveVector ?
                    { transform: `translate3d( ${ 0 < leftPinchMoveVector.X ? leftPinchMoveVector.X : 0 }px, 0, 0 )` } :
                    {} }>
                <div className={ Styles.time_tooltip_base }>
                    <div className={ Styles.time_tooltip }>
                        <div className={ Styles.triangle }></div>
                        { changeMillisec2Minute( trimStartTime ) }
                    </div>
                </div>
                <div className={ Styles.vertical_bar }></div>
                <div className={ Styles.vertical_bar }></div>
            </div>
            , [ leftPinchMoveVector ] )


        const RightPinchMemo = useMemo( () =>
            <div
                className={ clsx( Styles.pinch, Styles.right_pinch ) }
                ref={ rightPinchRef }
                { ...rightPinchTouchEvent }
                style={
                    rightPinchMoveVector ?
                        { transform: `translate3d( ${ rightPinchMoveVector.X < 0 ? rightPinchMoveVector.X : 0 }px, 0, 0 )` } :
                        {} }>
                <div className={ Styles.time_tooltip_base }>
                    <div className={ Styles.time_tooltip }>
                        <div className={ Styles.triangle }></div>
                        { changeMillisec2Minute( trimEndTime ) }
                    </div>
                </div>
                <div className={ Styles.vertical_bar }></div>
                <div className={ Styles.vertical_bar }></div>
            </div>
            , [ rightPinchMoveVector, changeVideoDuration ] )

        return (
            <div>
                <div className={ Styles.background }>
                    <div className={ Styles.title_container }>
                        <b className={ Styles.title }>
                            トリミング
                        </b>
                    </div>
                    { VideoMemo }
                    <div
                        className={ Styles.duration_bar }
                        ref={ durationBarRef }>
                        { RightPinchMemo }
                        { Capture.finished ?
                            Capture.captureList :
                            <div>
                                { Capture.drawCanvas }
                                <ActivityIndicator />
                            </div> }
                        { LeftPinchMemo }
                        <input
                            type="range"
                            className={ Styles.input_seek_bar }
                            step="1"
                            min="0"
                            max={ String( RangeMax ) }
                            value={ String( RangeMax * videoCurrentTime / videoDuration ) }
                            onTouchStart={ pauseVideo }
                            onMouseDown={ pauseVideo }
                            onChange={ e => {
                                onChangeRangeValue( Number( e.target.value ) )
                            } } />
                    </div>
                </div>
                <div style={ { display: "flex", justifyContent: "center", alignItems: "center", padding: 16 } }>
                    { trimIsDone ?
                        <span style={ { color: "#fff", fontSize: "1.1rem" } }>トリム済みです</span> :
                        <GeneralButton
                            title={
                                ffmpeg.loadEnd ?
                                    "トリミングする" :
                                    "ただいまトリミングの準備を\nしています。" }
                            style={ { width: 260 } }
                            onClick={ trim }
                            isDisabled={ !ffmpeg.loadEnd }
                            loading={ videoTrimming } /> }
                </div>
            </div >
        )
    }
